import React, { useState } from "react";
import styles from "./ChooseUsername.module.scss";
import { supabase, getSession } from "../../services/Account/Supabase";
import { useNavigate } from "react-router-dom";

const ChooseUsername: React.FC = () => {
	const navigate = useNavigate();
	const [username, setUsername] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Securely handle form submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		// ✅ Enhanced validation
		const sanitizedUsername = username.trim();

		if (!sanitizedUsername) {
			setError("Username cannot be empty.");
			return;
		}

		if (sanitizedUsername.length < 3 || sanitizedUsername.length > 20) {
			setError("Username must be between 3 and 20 characters.");
			return;
		}

		const usernameRegex = /^[a-zA-Z0-9_]+$/;
		if (!usernameRegex.test(sanitizedUsername)) {
			setError("Username can only contain letters, numbers, and underscores.");
			return;
		}

		setLoading(true);

		try {
			const session = await getSession();
			if (!session) {
				navigate("/login");
				return;
			}

			// ✅ Check username availability (safe query with Supabase)
			const { data: existingUsername, error: fetchUsernameError } = await supabase
				.from("user")
				.select("username")
				.eq("username", sanitizedUsername)
				.single();

			if (fetchUsernameError && fetchUsernameError.code !== "PGRST116") {
				setError("Error checking username availability.");
				setLoading(false);
				return;
			}

			if (existingUsername) {
				setError("This username is already taken.");
				setLoading(false);
				return;
			}

			// ✅ Check if user exists
			const { data: existingUser, error: fetchUserError } = await supabase
				.from("user")
				.select("uid")
				.eq("uid", session.user.id)
				.single();

			if (fetchUserError && fetchUserError.code !== "PGRST116") {
				setError("Error checking user record.");
				setLoading(false);
				return;
			}

			// ✅ Update or Insert user safely
			if (existingUser) {
				const { error: updateError } = await supabase
					.from("user")
					.update({ username: sanitizedUsername })
					.eq("uid", session.user.id);

				if (updateError) {
					setError("Failed to update username. Please try again.");
					setLoading(false);
					return;
				}
			} else {
				const { error: insertError } = await supabase
					.from("user")
					.insert({
						uid: session.user.id,
						username: sanitizedUsername,
					});

				if (insertError) {
					setError("Failed to create user. Please try again.");
					setLoading(false);
					return;
				}
			}

			// ✅ Redirect after success
			navigate("/home");
		} catch (err) {
			console.error("Unexpected error:", err);
			setError("An unexpected error occurred.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="app">
			<div className={styles.chooseUsernameContainer}>
				<h1>Choose Your Username</h1>
				<p>Please select a unique username to continue.</p>

				<form onSubmit={handleSubmit} className={styles.form}>
					<input
						type="text"
						placeholder="Enter your username"
						value={username}
						onChange={(e) => setUsername(e.target.value)}
						className={styles.input}
						disabled={loading}
						maxLength={20}
					/>
					<button type="submit" className={styles.submitButton} disabled={loading}>
						{loading ? "Saving..." : "Save Username"}
					</button>
				</form>

				{error && <p className={styles.error}>{error}</p>}
			</div>
		</div>
	);
};

export default ChooseUsername;
